import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";
import { pumpSchema } from "./pumps";

export const pumpStationListItemSchema = baseAssetWithTimeseriesSchema.extend({
  type: z.string(),
  name: z.string(),
  start_point: z.string().nullable(),
  end_point: z.string().nullable(),
  connection_serial: z.number().int().nullable(),
  allowed_flow_direction: z.string().nullable() /* backwards | forwards | both | closed */,
  capacity: z.number().nullable(),
  start_level: z.number().nullable(),
  stop_level: z.number().nullable(),
  start_level_delivery_side: z.number().nullable(),
  stop_level_delivery_side: z.number().nullable(),
  pumps: z.array(pumpSchema),
});

export type PumpStationListItem = z.infer<typeof pumpStationListItemSchema>;

export const pumpStationListSchema = paginatedSchema(pumpStationListItemSchema);
export type PumpStationList = z.infer<typeof pumpStationListSchema>;

export const pumpStationSchema = pumpStationListItemSchema;
export type PumpStation = PumpStationListItem;

export function usePumpStations(params = {}) {
  const url = combineUrlAndParams("/api/v4/pumpstations/", params);
  return useParsedQuery<PumpStationList>(
    ["lizard-api-v4", "pumpstations", url],
    () => fetchWithError(url),
    (json: unknown) => pumpStationListSchema.parse(json)
  );
}

export function usePumpStation(id: number) {
  return useParsedQuery<PumpStation>(
    ["lizard-api-v4", "pumpstations", id],
    () => fetchWithError(`/api/v4/pumpstations/${id}/`),
    (json: unknown) => pumpStationSchema.parse(json)
  );
}

export function useMultiplePumpStations(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "pumpstations", id],
        fetchFunction: () => fetchWithError(`/api/v4/pumpstations/${id}/`),
        parseFunction: (json: unknown) => pumpStationSchema.parse(json),
      };
    })
  );
}
